import { useCallback, useEffect, useState } from "react";
import useFetch from "hooks/useFetch";
import useSignalR from "hooks/useSignalR";
import { getFetchOptions } from "utils/fetch";

// Seeded from the page-load payload + SignalR updates so the publish button stays gated across reloads / user switches. Idle when draftId is falsy.
export const useDraftProcessingStatus = (draftId, initialCount = 0) => {
	const [count, setCount] = useState(initialCount);
	const { execute } = useFetch();
	const { connection, registerHandler, onConnected } = useSignalR();

	const refresh = useCallback(() => {
		if (!draftId) {
			return;
		}
		execute(`/api/v3/Course/Draft/${draftId}/processing-status`, getFetchOptions({ method: "GET" }), (data, error) => {
			if (!error && data) {
				setCount(data.count ?? data.Count ?? 0);
			}
		});
	}, [draftId, execute]);

	// Seed from the page-load payload rather than a dedicated REST snapshot on load. SignalR (below) supplies live
	// updates, and refresh() is still used for reconnect recovery and the reconcile poll.
	useEffect(() => {
		setCount(draftId ? (initialCount ?? 0) : 0);
	}, [draftId, initialCount]);

	useEffect(() => {
		if (!connection || !draftId) {
			return;
		}
		const groupName = `draft-processing-${draftId}`;

		// Skip the REST read on the first connect: the page-load seed is already current. A later full restart
		// (useSignalR onclose -> startConnection re-runs this callback) may have missed updates while disconnected,
		// so re-read then. The built-in auto-reconnect path is handled separately by onreconnected below.
		let hasJoinedOnce = false;

		onConnected(async (conn) => {
			await conn.invoke("JoinGroupAsync", groupName);
			if (hasJoinedOnce) {
				refresh();
			}
			else {
				hasJoinedOnce = true;
			}
		});

		const handleReconnect = async () => {
			await connection.invoke("JoinGroupAsync", groupName);
			refresh();
		};
		connection.onreconnected(handleReconnect);

		const unregister = registerHandler("DraftProcessingChanged", groupName, (message) => {
			const payload = message?.Payload ?? message?.payload ?? {};
			setCount(payload.count ?? payload.Count ?? 0);
		});

		return () => {
			unregister();
			connection.invoke("LeaveGroupAsync", groupName).catch(() => { /* connection may already be down */ });
		};
	}, [connection, draftId, registerHandler, onConnected, refresh]);

	return {
		isServerProcessing: count > 0,
		serverProcessingCount: count,
		refreshProcessingStatus: refresh
	};
};
